import Flex from 'components/ui/Flex';
import Radio from 'components/ui/Radio';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import {
    selectChangingShippingOption,
    selectSelectedShippingOptionId,
    selectShippingOption,
    selectShippingOptions,
} from 'store/reducers/cartSlice';
import styled from 'styled-components/macro';

export const ShippingOptions: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const shippingOptions = useAppSelector(selectShippingOptions);
    const selectedShippingOptionId = useAppSelector(selectSelectedShippingOptionId);
    const changing = useAppSelector(selectChangingShippingOption);

    if (!shippingOptions || shippingOptions.length === 0) return null;

    return (
        <OptionGroup>
            <VisuallyHiddenLegend>{t('shipment.options', 'Shipping options')}</VisuallyHiddenLegend>
            {shippingOptions.map((option) => (
                <ShippingOptionItem align="center" className="mb-3" key={option.id}>
                    <Radio
                        bgColor="fill"
                        checked={option.id === selectedShippingOptionId}
                        name="shippingOption"
                        addRightMargin={false}
                        disabled={changing}
                        onChange={() =>
                            option.id != null && dispatch(selectShippingOption({ shippingOptionId: option.id }))
                        }
                        label={
                            <ShippingOptionLabel>
                                <ShippingOptionText>
                                    <Text fontSize={14} className="f3-700">
                                        {option.name}
                                    </Text>
                                    {option.description && <Text fontSize={14}>{option.description}</Text>}
                                    {option.freeShippingThreshold && (
                                        <Text fontSize={12} light>
                                            {t('shipment.freeAbove', 'Free above {{amount}}', {
                                                amount: option.freeShippingThreshold,
                                            })}
                                        </Text>
                                    )}
                                </ShippingOptionText>
                                <ShippingOptionPrice fontSize={14} className="f3-700">
                                    {option.price ?? t('shipment.free', 'Free')}
                                </ShippingOptionPrice>
                            </ShippingOptionLabel>
                        }
                    />
                </ShippingOptionItem>
            ))}
        </OptionGroup>
    );
};
export default ShippingOptions;

const OptionGroup = styled.fieldset`
    border: 0;
    margin: 0;
    padding: 0;
    min-width: 0;
`;

const ShippingOptionItem = styled(Flex)`
    /* The radio's own flex row sizes to its content; fill the width so the price reaches the edge. */
    > * {
        flex: 1;
        min-width: 0;
    }

    label {
        cursor: pointer;
        flex: 1;
        min-width: 0;
        margin-left: 0.75rem;
        margin-right: 0;
    }
`;

const ShippingOptionLabel = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
`;

const ShippingOptionText = styled.span`
    display: block;
    flex: 1;
    min-width: 0;
`;

const ShippingOptionPrice = styled(Text)`
    flex-shrink: 0;
    text-align: right;
    white-space: nowrap;
`;

const VisuallyHiddenLegend = styled.legend`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;
